import { IonIcon } from "@ionic/react";
import { heartOutline } from "ionicons/icons";
import { motion } from "motion/react";
import React, { useMemo } from "react";
import { useReducedMotion } from "../../../hooks/useReducedMotion.hook";
import { getUIColorPalette } from "../../../theme/ColorPalette";
import { isUiMotionEnabled } from "../../shared/motion/uiMotion.tools";
import { buildSavedItineraryEmptyStateEnterTransition } from "./savedItineraryList.animation.tools";

/** Visual variant for the saved-itinerary empty state. */
export type SavedItinerariesEmptyStateVariant =
  | "noSaved" |
  "noUpcoming" |
  "filteredEmpty";

interface SavedItinerariesEmptyStateProps {

    /** Whether dark overlay palette tokens should be used. */
    "isDarkUi": boolean;

    /** Which empty scenario to present. */
    "variant": SavedItinerariesEmptyStateVariant;
}

/**
 * Empty-state panel for the saved-itinerary list.
 *
 * @param props Empty-state props.
 * @returns Centered message.
 */
const SavedItinerariesEmptyState: React.FC<SavedItinerariesEmptyStateProps> = ( {
    isDarkUi,
    variant,
} ) => {
    const reducedMotion = useReducedMotion();
    const animationsEnabled = isUiMotionEnabled( reducedMotion );
    const palette = useMemo( () => getUIColorPalette( isDarkUi ), [ isDarkUi ] );

    const title = variant === "noSaved"
        ? "Aucun itinéraire sauvegardé"
        : variant === "noUpcoming"
            ? "Aucun itinéraire à venir"
            : "Aucun itinéraire dans cette catégorie";

    const description = variant === "noSaved"
        ? "Lancez une recherche, ouvrez un trajet puis enregistrez-le avec le bouton cœur."
        : variant === "noUpcoming"
            ? "Tous vos itinéraires sauvegardés sont dans le passé."
            : "Essayez un autre filtre.";

    const content = (
        <>
            <div
                className="mb-3 flex h-12 w-12 items-center justify-center rounded-full"
                style={{ "backgroundColor": palette.selectorIdleBackground }}
            >
                <IonIcon
                    aria-hidden="true"
                    icon={heartOutline}
                    style={{
                        "color":    palette.brandPrimary,
                        "fontSize": "24px",
                    }}
                />
            </div>
            <p
                className="text-sm font-semibold"
                style={{ "color": palette.overlayTextPrimary }}
            >
                {title}
            </p>
            <p
                className="mt-2 max-w-xs text-xs leading-relaxed"
                style={{ "color": palette.overlayTextSecondary }}
            >
                {description}
            </p>
        </>
    );

    if ( !animationsEnabled ) {
        return (
            <div
                className="flex flex-col items-center px-4 py-10 text-center"
                id="saved-list-empty"
            >
                {content}
            </div>
        );
    }

    return (
        <motion.div
            animate={{ "opacity": 1 }}
            className="flex flex-col items-center px-4 py-10 text-center"
            id="saved-list-empty"
            initial={{ "opacity": 0 }}
            transition={buildSavedItineraryEmptyStateEnterTransition( reducedMotion )}
        >
            {content}
        </motion.div>
    );
};

export default SavedItinerariesEmptyState;
